import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import './ContrailFrame.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Utility function for formatting dates and times in UTC
const formatDateUTC = (date) => formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
const formatTimeUTC = (date) => formatInTimeZone(date, 'UTC', 'HH:mm:ss');

const ContrailFrame = ({ currentDateTime, visible }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false); // State for loading
    const dataCache = useRef({}); // Cache for fetched data
    const inProgressCache = useRef(new Set()); // To track ongoing requests

    const isDataEmpty = (data) => {
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data).length === 0;
        }
        return !data; // This will catch null, undefined, '', 0, false
    };

    const fetchFrameForCurrentTime = useCallback(async (dateStr, timeStr) => {
        const cacheKey = `${dateStr} ${timeStr}`;
        if (dataCache.current[cacheKey]) {
            setImageUrl(dataCache.current[cacheKey]);
            return;
        }

        if (inProgressCache.current.has(cacheKey)) {
            return; // Skip fetching if already in progress
        }

        inProgressCache.current.add(cacheKey);
        setLoading(true); // Start loading

        try {
            const response = await fetch(`${API_BASE_URL}/contrail_frame?date_str=${dateStr}&time_str=${timeStr}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            if (!isDataEmpty(data) && data.url) {
                dataCache.current[cacheKey] = data.url; // Cache the result
                setImageUrl(data.url);
            } else {
                setImageUrl(null);
            }
        } catch (error) {
            console.error(`Error fetching frame for ${dateStr} ${timeStr}:`, error);
            setImageUrl(null);
        } finally {
            setLoading(false); // Stop loading
            inProgressCache.current.delete(cacheKey);
        }
    }, []);

    const debouncedFetchFrameForCurrentTime = useMemo(
        () => debounce(fetchFrameForCurrentTime, 300),
        [fetchFrameForCurrentTime]
    );

    useEffect(() => {
        if (!visible) return;

        const currentDateStr = formatDateUTC(currentDateTime);
        const currentTimeStr = formatTimeUTC(currentDateTime);

        // Fetch frame every time the currentDateTime changes
        debouncedFetchFrameForCurrentTime(currentDateStr, currentTimeStr);

        return () => {
            debouncedFetchFrameForCurrentTime.cancel();
        };
    }, [currentDateTime, visible, debouncedFetchFrameForCurrentTime]);

    if (!visible) {
        return null;
    }

    return (
        <div className="popup-container">
            {loading && (
                <p>Contrail Picture Loading...</p>
                // <div className="loading-screen">
                //     <div className="spinner"> </div>
                // </div>
            )}

            {!loading && imageUrl ? (
                <div>
                    <img 
                        src={imageUrl} 
                        alt="Closest Contrail Frame" 
                        style={{ width: '100%' }} 
                        onClick={() => window.open(imageUrl, '_blank')}
                    />
                </div>
            ) : (
                !loading && <p>No frame available for the selected date and time.</p>
            )}
        </div>
    );
};

export default ContrailFrame;
